// @ts-nocheck
import * as React from 'react';
// ConsolePage — main Counsellor Console page (Concept A v2).
// Three-column layout: Pipeline | Workbench | TrustRail.

import { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { useAuth } from 'core/context/AuthContext/AuthContextProvider';
import useTweaks from '../hooks/useTweaks';
import ScalingFrame from '../components/layout/ScalingFrame';
import Sidebar from '../components/layout/Sidebar';
import TopBar from '../components/layout/TopBar';
import DemoTriggerModal from '../components/feedback/DemoTriggerModal';
import Toasts from '../components/feedback/Toasts';
import TweaksPanel from '../components/tweaks/TweaksPanel';
import { TweakSection, TweakToggle, TweakRadio } from '../components/tweaks/TweakControls';
import Pipeline from './Pipeline';
import Workbench from './Workbench';
import WorkbenchV2 from './WorkbenchV2';
import TrustRail from './TrustRail';
import { rankPillars } from '../data/rankPillars';
import { fetchAdmissionLeadDetails } from './services/leadsApi';
import { fetchWhatsAppDemoStatus, requestWhatsAppDemoApprovalQr, sendWhatsAppDemo } from './services/whatsappDemoApi';
import { useAdmissionsLeads } from '../context/AdmissionsLeadsContext';
import './ConsolePage.css';

const TWEAK_DEFAULTS = {
  aiOn: true,
  pipelineSize: "busy",
};

function LeadViewSkeleton() {
  return (
    <section className="A-col A-col--bench A-view-skeleton" role="status" aria-label="Loading lead details">
      <div className="A-view-skeleton__hero">
        <span className="A-skeleton A-view-skeleton__eyebrow" />
        <span className="A-skeleton A-view-skeleton__title" />
        <span className="A-skeleton A-view-skeleton__copy" />
        <span className="A-skeleton A-view-skeleton__copy A-view-skeleton__copy--short" />
      </div>
      <div className="A-view-skeleton__heading"><span className="A-skeleton" /><span className="A-skeleton" /></div>
      {Array.from({ length: 4 }).map((_, index) => (
        <div className="A-view-skeleton__card" key={index} aria-hidden="true">
          <span className="A-skeleton A-view-skeleton__icon" />
          <span className="A-view-skeleton__card-copy">
            <span className="A-skeleton" />
            <span className="A-skeleton" />
          </span>
          <span className="A-skeleton A-view-skeleton__action" />
        </div>
      ))}
    </section>
  );
}

export default function ConsolePage({ variant = "default" }) {
  const authData = useAuth();
  const instituteId = authData?.INSTITUTE_ID;
  const branchId = authData?.BRANCH_ID;
  const [t, setTweak] = useTweaks(TWEAK_DEFAULTS);
  const aiOn = !!t.aiOn;
  const {
    leads: leadParents,
    loading: isLoadingLeads,
    error: leadsError,
    refreshLeads,
    mergeLead,
    setSelectedLeadId,
  } = useAdmissionsLeads();
  const [loadingLeadId, setLoadingLeadId] = useState(null);
  const [leadDetailsError, setLeadDetailsError] = useState('');
  const [whatsAppConfirmedByLead, setWhatsAppConfirmedByLead] = useState({});
  const leadRequestId = useRef(0);

  // Filter pipeline by density
  const parents = useMemo(() => {
    if (t.pipelineSize === "light") return leadParents.slice(0, 3);
    return leadParents;
  }, [leadParents, t.pipelineSize]);

  const [activeParentId, setActiveParentId] = useState(null);
  const handleLeadSelect = useCallback(async (parentId) => {
    const parent = parents.find((item) => item.id === parentId);
    if (!parent) return;

    setActiveParentId(parentId);
    setSelectedLeadId(parentId);
    setLoadingLeadId(parentId);
    setLeadDetailsError('');
    const requestId = ++leadRequestId.current;
    try {
      const details = await fetchAdmissionLeadDetails(parent.leadId, {
        INSTITUTE_ID: instituteId,
        BRANCH_ID: branchId,
      });
      if (requestId !== leadRequestId.current) return;

      mergeLead(parentId, details);
      setDemosByParent((current) => ({
        ...current,
        [parentId]: (details.demosTriggered || []).map((featureId, index) => ({
          id: `detail-${parentId}-${featureId}`,
          featureId,
          lang: details.language || parent.language,
          phone: details.phone || parent.phone,
          note: '',
          at: Date.now() - index,
        })),
      }));
    } catch (error) {
      if (requestId === leadRequestId.current) {
        setLeadDetailsError(error?.response?.data?.message || error?.message || 'Unable to load lead details.');
      }
    } finally {
      if (requestId === leadRequestId.current) setLoadingLeadId(null);
    }
  }, [parents, instituteId, branchId, mergeLead, setSelectedLeadId]);

  useEffect(() => {
    if (isLoadingLeads || parents.length === 0) return;
    if (!activeParentId || !parents.some((parent) => parent.id === activeParentId)) {
      handleLeadSelect(parents[0].id);
    }
  }, [parents, activeParentId, isLoadingLeads, handleLeadSelect]);

  // Demo trigger state
  const [pendingPillar, setPendingPillar] = useState(null);

  // Demos-by-parent
  const [demosByParent, setDemosByParent] = useState({});
  useEffect(() => {
    setDemosByParent((current) => {
      const seed = { ...current };
      leadParents.forEach((p) => {
        if (seed[p.id]) return;
        seed[p.id] = (p.demosTriggered || []).map((fid, i) => ({
        id: `seed-${p.id}-${i}`,
        featureId: fid,
        lang: p.language,
        phone: p.phone,
        note: "",
        at: Date.now() - (i + 1) * 1000 * 60 * 8,
        }));
      });
      return seed;
    });
  }, [leadParents]);

  // Toasts
  const [toasts, setToasts] = useState([]);
  const pushToast = (toast) => {
    const id = `t-${Date.now()}-${Math.random().toString(36).slice(2, 5)}`;
    setToasts((arr) => [...arr, { id, ...toast }]);
    setTimeout(() => setToasts((arr) => arr.filter((x) => x.id !== id)), 4800);
  };

  // Trigger flow
  const onTrigger = (pillar) => {
    setPendingPillar(pillar);
  };

  const onRequestDemo = ({ phone }) => requestWhatsAppDemoApprovalQr({ mobile: phone });
  const onCheckDemoStatus = async ({ phone }) => {
    const status = await fetchWhatsAppDemoStatus(phone);
    const confirmed = Boolean(status?.data?.confirmed);
    if (activeParentId) {
      setWhatsAppConfirmedByLead((current) => ({ ...current, [activeParentId]: confirmed }));
    }
    return confirmed;
  };

  const onConfirmTrigger = async ({ phone, lang, note, assetTypes }) => {
    if (!pendingPillar) throw new Error('Select a demo before sending.');
    const parent = parents.find((p) => p.id === activeParentId);
    if (!parent || !activeParentId) throw new Error('Select a parent before sending.');
    const content = pendingPillar.generatedContent || {};
    const info = authData?.USER_INFO?.[0] || {};
    const common = {
      MOBILE: phone,
      CAPTION: content.TITLE || content.HEADING || pendingPillar.name || '',
      BODY: note || content.MESSAGE || content.ISSUE || '',
      CALLBACK_DATA: JSON.stringify({ lead_id: parent.leadId, feature_id: pendingPillar.id }),
      INSTITUTE_ID: Number(authData?.INSTITUTE_ID || info.INSTITUTE_ID || 0),
      BRANCH_ID: Number(authData?.BRANCH_ID || info.BRANCH_ID || 0),
      DATABASE: authData?.DATABASE || info.DATABASE || '',
      USER_ID: Number(authData?.USER_ID || info.USER_ID || 0),
    };
    const fileName = (url = '') => {
      try { return decodeURIComponent(new URL(url).pathname.split('/').pop() || ''); } catch { return ''; }
    };
    const messages = assetTypes.map((assetType) => {
      const type = assetType.toLowerCase();
      const link = type === 'video' ? content.VIDEO_URL : type === 'audio' ? content.AUDIO_URL : '';
      return { ...common, TYPE: type, LINK: link || '', FILENAME: fileName(link) };
    });

    const response = await sendWhatsAppDemo(messages);
    setDemosByParent((dbp) => ({
      ...dbp,
      [activeParentId]: [
        ...(dbp[activeParentId] || []),
        { id: `d-${Date.now()}`, featureId: pendingPillar.id, lang, phone, note, assetTypes, at: Date.now() },
      ],
    }));
    pushToast({
      title: response?.message || `${pendingPillar.name} sent to ${parent.name}`,
      sub: `${assetTypes.join(', ')} · WhatsApp ${phone} · in ${lang}`,
    });
    setPendingPillar(null);
  };

  // Sidebar selection
  const [railActive, setRailActive] = useState("home");

  const activeParent = parents.find((p) => p.id === activeParentId) || parents[0] || null;
  const ranked = useMemo(() => rankPillars(activeParent), [activeParent]);
  const Bench = variant === "v2" ? WorkbenchV2 : Workbench;

  return (
    <div className="frame-shell">
      <ScalingFrame>
        <Sidebar activeId={railActive} onSelect={setRailActive} />
        <TopBar activeParent={activeParent} concept="A" />
        <div className="work">
          <div className="A-grid">
            <Pipeline
              parents={parents}
              activeId={activeParent?.id || null}
              onSelect={handleLeadSelect}
              demosByParent={demosByParent}
              isLoading={isLoadingLeads}
              error={leadsError}
              onRetry={() => refreshLeads().catch(() => undefined)}
              loadingLeadId={loadingLeadId}
              detailError={leadDetailsError}
            />
            {(isLoadingLeads || loadingLeadId) ? <LeadViewSkeleton /> : activeParent?.detailsLoaded ? <Bench
              parent={activeParent}
              ranked={ranked}
              demos={demosByParent[activeParent.id] || []}
              onTrigger={onTrigger}
              aiOn={aiOn}
            /> : <section className="A-col A-col--bench A-bench-empty">
              <strong>{activeParent ? 'Select this lead to view details' : 'No lead selected'}</strong>
              <span>{leadDetailsError || leadsError || (activeParent ? 'Click the lead card to load its Admissions Assured data.' : 'New admission enquiries will appear here.')}</span>
            </section>}
            <TrustRail />
          </div>
        </div>
        <DemoTriggerModal
          open={!!pendingPillar}
          parent={activeParent}
          pillar={pendingPillar}
          onClose={() => setPendingPillar(null)}
          onRequestDemo={onRequestDemo}
          onCheckStatus={onCheckDemoStatus}
          isWhatsAppConfirmed={Boolean(activeParent && whatsAppConfirmedByLead[activeParent.id])}
          onConfirm={onConfirmTrigger}
        />
        <Toasts items={toasts} onDismiss={(id) => setToasts((arr) => arr.filter((x) => x.id !== id))} />
      </ScalingFrame>

      <TweaksPanel>
        <TweakSection label="AI Copilot" />
        <TweakToggle
          label="AI ranking + brief"
          value={t.aiOn}
          onChange={(v) => setTweak("aiOn", v)}
        />
        <div style={{ padding: "0 14px 10px", fontSize: 11, color: "var(--fg-tertiary)", lineHeight: 1.45 }}>
          When on, the AI ranks the features for each parent and writes a counsellor brief. Off shows the raw inventory.
        </div>

        <TweakSection label="Pipeline density" />
        <TweakRadio
          label="Today's pipeline"
          value={t.pipelineSize}
          options={["light", "busy"]}
          onChange={(v) => setTweak("pipelineSize", v)}
        />
      </TweaksPanel>
    </div>
  );
}
